/**
 * Reprioritization Trigger Panel — Doc 14 §3, Doc 17
 *
 * Reads reprioritization_triggers for the plan cycle.
 * Shows open triggers with actions to acknowledge / close.
 * Resolved triggers collapse to summary.
 */

import { useEffect, useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { RefreshCw, ChevronRight, ChevronDown, CheckCheck, X, Loader2, Plus, Users, ExternalLink } from 'lucide-react';
import {
  acknowledgeReprioritizationTrigger,
  closeReprioritizationTrigger,
  recommendReprioritization,
} from '../../lib/planningService';
import type { ReprioritizationTriggerRow } from '../../lib/planningService';
import { getTypedSupabaseClient } from '../../lib/typedSupabaseClient';
import {
  buildReRankSessionPrefill,
  resolvePlanItemsForReRank,
  resolvePrioritizationCycleId,
} from '../../lib/reprioritizationSessionPrefill';

/**
 * Break 5 of the decision→…→reprioritization chain audit: the context every
 * trigger's row-level actions share — the plan's current item list (for
 * "Start a session to re-rank") and, when resolvable, the prioritization
 * Cycle the plan's input traces back to (for "Open a catchball round").
 * Fetched once per plan cycle, not once per trigger row.
 */
interface ReRankContext {
  loading: boolean;
  items: { id: string; title: string }[];
  prioritizationCycleId: string | null;
  /** Open continuous-prioritization signals for the resolved cycle (read-only; no app write path exists yet — see module doc). */
  openSignals: { id: string; message: string; severity: string; signal_type: string }[];
}

const EMPTY_CONTEXT: ReRankContext = {
  loading: true,
  items: [],
  prioritizationCycleId: null,
  openSignals: [],
};

function useReRankContext(planCycleId: string): ReRankContext {
  const [ctx, setCtx] = useState<ReRankContext>(EMPTY_CONTEXT);

  useEffect(() => {
    let cancelled = false;
    setCtx(EMPTY_CONTEXT);

    void (async () => {
      const db = getTypedSupabaseClient();
      const [{ data: cycle }, items] = await Promise.all([
        db
          .from('plan_cycles')
          .select('input_type, input_reference_id, workspace_id')
          .eq('id', planCycleId)
          .maybeSingle(),
        resolvePlanItemsForReRank(planCycleId),
      ]);
      if (cancelled) return;

      const prioritizationCycleId = cycle
        ? await resolvePrioritizationCycleId({
            inputType: cycle.input_type,
            inputReferenceId: cycle.input_reference_id,
          })
        : null;
      if (cancelled) return;

      // Read-only surfacing of reprioritization_signals (603) — today reachable
      // only through the API edge function; the app has never read this table.
      // RLS carries a workspace-scoped read policy, so this is a real read
      // path, not a new one being invented here.
      let openSignals: ReRankContext['openSignals'] = [];
      if (prioritizationCycleId && cycle?.workspace_id) {
        const { data } = await db
          .from('reprioritization_signals')
          .select('id, message, severity, signal_type')
          .eq('workspace_id', cycle.workspace_id)
          .eq('cycle_id', prioritizationCycleId)
          .eq('status', 'open');
        openSignals = data ?? [];
      }
      if (cancelled) return;

      setCtx({ loading: false, items, prioritizationCycleId, openSignals });
    })();

    return () => {
      cancelled = true;
    };
  }, [planCycleId]);

  return ctx;
}

// ─── Status config ────────────────────────────────────────────────────────────

const STATUS_CONFIG: Record<string, { label: string; bg: string; text: string }> = {
  open:         { label: 'Open',         bg: 'bg-[var(--mustard-dim)]',  text: 'text-[var(--mustard)]' },
  acknowledged: { label: 'Acknowledged', bg: 'bg-[var(--slate-dim)]',   text: 'text-[var(--text-sec)]' },
  closed:       { label: 'Closed',       bg: 'bg-[var(--green-dim)]',  text: 'text-[var(--green)] dark:text-[var(--text-ter)]' },
};

function StatusBadge({ status }: { status: string }) {
  const cfg = STATUS_CONFIG[status] ?? STATUS_CONFIG.open;
  return (
    <span className={`inline-flex text-xs font-medium px-2 py-0.5 rounded-full ${cfg.bg} ${cfg.text}`}>
      {cfg.label}
    </span>
  );
}

// ─── Single trigger row ───────────────────────────────────────────────────────

function TriggerRow({
  trigger,
  reRank,
  onChanged,
}: {
  trigger: ReprioritizationTriggerRow;
  reRank: ReRankContext;
  onChanged: () => void;
}) {
  const [expanded, setExpanded] = useState(trigger.status === 'open');
  const [acting, setActing] = useState(false);
  const [actError, setActError] = useState<string | null>(null);
  const navigate = useNavigate();

  const reason = trigger.reason ?? {};
  const summary = (reason.summary ?? reason.reason ?? reason.description ?? '') as string;

  const handleStartSession = () => {
    const prefill = buildReRankSessionPrefill(trigger.reason, reRank.items);
    navigate('/prioritization/sessions/new', { state: { prefill } });
  };

  const handleAction = async (action: 'acknowledge' | 'close') => {
    setActing(true);
    setActError(null);
    const fn = action === 'acknowledge' ? acknowledgeReprioritizationTrigger : closeReprioritizationTrigger;
    const { error } = await fn(trigger.id);
    setActing(false);
    if (error) { setActError(error); return; }
    onChanged();
  };

  const isResolved = trigger.status === 'closed';

  return (
    <div className={`border  overflow-hidden ${isResolved ? 'border-[var(--rule)]' : 'border-[var(--mustard)]'}`}>
      <button
        type="button"
        onClick={() => setExpanded((v) => !v)}
        className="w-full flex items-center gap-3 px-4 py-3 text-left hover:bg-[var(--surface)] transition-colors"
      >
        {expanded
          ? <ChevronDown className="h-3.5 w-3.5 text-[var(--text-ter)] flex-shrink-0" />
          : <ChevronRight className="h-3.5 w-3.5 text-[var(--text-ter)] flex-shrink-0" />
        }
        <RefreshCw className={`h-3.5 w-3.5 flex-shrink-0 ${isResolved ? 'text-[var(--text-ter)]' : 'text-[var(--mustard)]'}`} />
        <span className="text-sm text-[var(--text-sec)] truncate flex-1">{summary || 'Re-prioritization recommended'}</span>
        <span className="text-xs text-[var(--text-ter)] mr-2">
          {new Date(trigger.triggered_at).toLocaleDateString()}
        </span>
        <StatusBadge status={trigger.status} />
      </button>

      {expanded && (
        <div className="px-4 pb-3 pt-1 space-y-2 border-t border-[var(--rule)] bg-[var(--surface)]/50">
          {trigger.triggered_by && (
            <p className="text-xs text-[var(--text-ter)]">
              <span className="font-medium text-[var(--text-sec)]">Triggered by: </span>{trigger.triggered_by}
            </p>
          )}
          {typeof reason === 'object' && Object.entries(reason).filter(([k]) => !['summary', 'reason', 'description'].includes(k)).length > 0 && (
            <div className="text-xs text-[var(--text-ter)]">
              {Object.entries(reason)
                .filter(([k]) => !['summary', 'reason', 'description'].includes(k))
                .map(([k, v]) => (
                  <p key={k}><span className="font-medium text-[var(--text-sec)]">{k}: </span>{String(v)}</p>
                ))}
            </div>
          )}

          {/* Break 5: this recommendation could be acknowledged/closed and lead
              nowhere. Give it two live paths: a pre-filled session, and — when
              the plan's input resolves to a prioritization Cycle — a catchball
              round. Disabled-with-reason when the context isn't there. */}
          <div className="flex flex-wrap items-center gap-2 pt-1">
            <button
              type="button"
              disabled={reRank.loading || reRank.items.length === 0}
              onClick={handleStartSession}
              title={
                reRank.loading
                  ? 'Resolving this plan’s current items…'
                  : reRank.items.length === 0
                    ? 'No items resolved from this plan’s current version — nothing to re-rank'
                    : undefined
              }
              className="inline-flex items-center gap-1.5 px-3 py-1.5 border border-[var(--slate)] text-[var(--text-ter)] text-xs font-medium hover:bg-[var(--slate-dim)] disabled:opacity-40 disabled:cursor-not-allowed"
            >
              <RefreshCw className="h-3.5 w-3.5" />
              Start a session to re-rank
            </button>
            {reRank.loading ? null : reRank.prioritizationCycleId ? (
              <Link
                to={`/prioritization/cascade/compose/${reRank.prioritizationCycleId}`}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 border border-[var(--rule)] text-[var(--text-sec)] text-xs font-medium hover:bg-[var(--hover-tint)]"
              >
                <Users className="h-3.5 w-3.5" />
                Open a catchball round
              </Link>
            ) : (
              <span
                title="This plan's input doesn't resolve to a prioritization Cycle — open a catchball round from the Cascade dashboard instead"
                className="inline-flex items-center gap-1.5 px-3 py-1.5 border border-[var(--rule)] text-[var(--text-ter)] text-xs font-medium opacity-60 cursor-not-allowed"
              >
                <Users className="h-3.5 w-3.5" />
                Open a catchball round
              </span>
            )}
          </div>

          {/* Actions — only for non-closed triggers */}
          {trigger.status !== 'closed' && (
            <div className="flex items-center gap-2 pt-1">
              {trigger.status === 'open' && (
                <button
                  type="button"
                  disabled={acting}
                  onClick={() => void handleAction('acknowledge')}
                  className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-[var(--slate)] text-white text-xs font-medium hover:bg-[var(--slate)] disabled:opacity-50"
                >
                  {acting ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <CheckCheck className="h-3.5 w-3.5" />}
                  Acknowledge
                </button>
              )}
              <button
                type="button"
                disabled={acting}
                onClick={() => void handleAction('close')}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 border border-[var(--rule)] text-[var(--text-sec)] text-xs font-medium hover:bg-[var(--hover-tint)] disabled:opacity-50"
              >
                {acting ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <X className="h-3.5 w-3.5" />}
                Close trigger
              </button>
              {actError && <span className="text-xs text-[var(--coral)] ml-2">{actError}</span>}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// ─── Panel ────────────────────────────────────────────────────────────────────

// ─── Recommend modal ─────────────────────────────────────────────────────────

function RecommendModal({
  planCycleId,
  onClose,
  onCreated,
}: {
  planCycleId: string;
  onClose: () => void;
  onCreated: () => void;
}) {
  const [summary, setSummary] = useState('');
  const [detail, setDetail] = useState('');
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  const handleSave = async () => {
    if (!summary.trim()) { setErr('Summary is required.'); return; }
    setSaving(true);
    setErr(null);
    const { error } = await recommendReprioritization(planCycleId, {
      summary: summary.trim(),
      detail: detail.trim() || undefined,
    });
    setSaving(false);
    if (error) { setErr(error); return; }
    onCreated();
    onClose();
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/40 backdrop-blur-sm">
      <div className="bg-[var(--bg-raised)] shadow-2xl border border-[var(--rule)]/80 w-full max-w-md">
        <div className="flex items-center justify-between px-6 py-4 border-b border-[var(--rule)]">
          <div className="flex items-center gap-2">
            <RefreshCw className="h-5 w-5 text-[var(--mustard)]" />
            <h3 className="text-base font-semibold text-[var(--text-pri)]">Recommend reprioritization</h3>
          </div>
          <button type="button" onClick={onClose} className="p-1 rounded hover:bg-[var(--hover-tint)]">
            <X className="h-5 w-5 text-[var(--text-ter)]" />
          </button>
        </div>
        <div className="px-6 py-4 space-y-4">
          <p className="text-sm text-[var(--text-ter)]">
            Use when constraints have become value-invalidating and the existing priority list no longer makes sense in context. This is a governance act — it creates an audited trigger for review.
          </p>
          <div>
            <label className="block text-sm font-medium text-[var(--text-sec)] mb-1">
              Reason summary <span className="text-[var(--coral)]">*</span>
            </label>
            <input
              type="text"
              value={summary}
              onChange={(e) => setSummary(e.target.value)}
              placeholder="e.g. New regulatory obligation invalidates top 3 priorities"
              className="w-full border border-[var(--rule)] px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-[var(--text-sec)] mb-1">Detail (optional)</label>
            <textarea
              rows={3}
              value={detail}
              onChange={(e) => setDetail(e.target.value)}
              placeholder="Additional context, constraints, or evidence…"
              className="w-full border border-[var(--rule)] px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 resize-none"
            />
          </div>
          {err && <p className="text-sm text-[var(--coral)]">{err}</p>}
        </div>
        <div className="flex justify-end gap-3 px-6 py-4 border-t border-[var(--rule)]">
          <button type="button" onClick={onClose} className="px-4 py-2 border border-[var(--rule)] text-sm font-medium text-[var(--text-sec)] hover:bg-[var(--surface)]">
            Cancel
          </button>
          <button
            type="button"
            onClick={() => void handleSave()}
            disabled={saving || !summary.trim()}
            className="px-4 py-2 bg-[var(--mustard)] text-white text-sm font-medium hover:bg-[var(--mustard)] disabled:opacity-50 flex items-center gap-2"
          >
            {saving && <Loader2 className="h-4 w-4 animate-spin" />}
            Recommend reprioritization
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── Panel ────────────────────────────────────────────────────────────────────

export interface ReprioritizationTriggerPanelProps {
  planCycleId: string;
  triggers: ReprioritizationTriggerRow[];
  onChanged: () => void;
}

export function ReprioritizationTriggerPanel({
  planCycleId,
  triggers,
  onChanged,
}: ReprioritizationTriggerPanelProps) {
  const [showRecommend, setShowRecommend] = useState(false);
  const open = triggers.filter((t) => t.status === 'open');
  const reRank = useReRankContext(planCycleId);

  // Always show the panel (even with no triggers) so the "Recommend" action is accessible
  if (triggers.length === 0 && !showRecommend) {
    return (
      <div className="bg-[var(--bg-raised)] border border-[var(--rule)] px-5 py-4">
        <div className="flex items-center justify-between">
          <h2 className="text-sm font-semibold text-[var(--text-pri)] flex items-center gap-2">
            <RefreshCw className="h-4 w-4 text-[var(--text-ter)]" />
            Reprioritization
          </h2>
          <button
            type="button"
            onClick={() => setShowRecommend(true)}
            className="inline-flex items-center gap-1 text-xs text-[var(--mustard)] hover:text-[var(--mustard)] font-medium"
          >
            <Plus className="h-3.5 w-3.5" />
            Recommend
          </button>
        </div>
        <p className="text-sm text-[var(--text-ter)] mt-2">
          No reprioritization triggers. Use Recommend if constraints have become value-invalidating.
        </p>
        {showRecommend && (
          <RecommendModal planCycleId={planCycleId} onClose={() => setShowRecommend(false)} onCreated={onChanged} />
        )}
      </div>
    );
  }

  return (
    <div className="bg-[var(--bg-raised)] border border-[var(--rule)] px-5 py-4 space-y-3">
      <div className="flex items-center justify-between">
        <h2 className="text-sm font-semibold text-[var(--text-pri)] flex items-center gap-2">
          <RefreshCw className="h-4 w-4 text-[var(--text-ter)]" />
          Reprioritization Triggers
          {open.length > 0 && (
            <span className="ml-1 text-xs font-medium px-2 py-0.5 rounded-full bg-[var(--mustard-dim)] text-[var(--mustard)]">
              {open.length} open
            </span>
          )}
        </h2>
        <div className="flex items-center gap-3">
          <button
            type="button"
            onClick={() => setShowRecommend(true)}
            className="inline-flex items-center gap-1 text-xs text-[var(--mustard)] hover:text-[var(--mustard)] font-medium"
          >
            <Plus className="h-3.5 w-3.5" />
            Recommend
          </button>
          <span className="text-xs text-[var(--text-ter)]">{triggers.length} total</span>
        </div>
      </div>

      <div className="space-y-1.5">
        {/* Open first */}
        {open.map((t) => (
          <TriggerRow key={t.id} trigger={t} reRank={reRank} onChanged={onChanged} />
        ))}
        {/* Resolved */}
        {triggers.filter((t) => t.status !== 'open').length > 0 && (
          <details className="group">
            <summary className="cursor-pointer text-xs text-[var(--text-ter)] hover:text-[var(--text-sec)] select-none list-none flex items-center gap-1 pt-1">
              <ChevronRight className="h-3 w-3 group-open:rotate-90 transition-transform" />
              {triggers.filter((t) => t.status !== 'open').length} resolved
            </summary>
            <div className="mt-1.5 space-y-1.5">
              {triggers.filter((t) => t.status !== 'open').map((t) => (
                <TriggerRow key={t.id} trigger={t} reRank={reRank} onChanged={onChanged} />
              ))}
            </div>
          </details>
        )}
      </div>

      {/* Continuous-prioritization signals (mig 603): today reachable only
          through the API edge function — see module doc. Read-only; no write
          action rides this panel. */}
      {reRank.openSignals.length > 0 && (
        <details className="group border-t border-[var(--rule)] pt-2">
          <summary className="cursor-pointer text-xs text-[var(--text-ter)] hover:text-[var(--text-sec)] select-none list-none flex items-center gap-1">
            <ChevronRight className="h-3 w-3 group-open:rotate-90 transition-transform" />
            {reRank.openSignals.length} continuous-prioritization signal
            {reRank.openSignals.length !== 1 ? 's' : ''} on this cycle
            <ExternalLink className="h-3 w-3 text-[var(--text-ter)]" />
          </summary>
          <ul className="mt-1.5 space-y-1 text-xs text-[var(--text-ter)]">
            {reRank.openSignals.map((s) => (
              <li key={s.id} className="flex items-start gap-1.5">
                <span
                  className={`mt-0.5 flex-shrink-0 w-1.5 h-1.5 rounded-full ${
                    s.severity === 'high'
                      ? 'bg-[var(--coral)]'
                      : s.severity === 'medium'
                        ? 'bg-[var(--mustard)]'
                        : 'bg-[var(--text-ter)]'
                  }`}
                />
                <span>{s.message}</span>
              </li>
            ))}
          </ul>
        </details>
      )}

      {showRecommend && (
        <RecommendModal
          planCycleId={planCycleId}
          onClose={() => setShowRecommend(false)}
          onCreated={() => { setShowRecommend(false); onChanged(); }}
        />
      )}
    </div>
  );
}
